import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import HomeNav from "../navigations/homenav";
import Footer from "../navigations/Footer";
import { useLanguageContext } from "../navigations/homenav";
import {
  CalendarIcon,
  UserIcon,
  ArrowRightIcon,
  MegaphoneIcon,
  ScaleIcon,
  AcademicCapIcon,
  UsersIcon,
  NewspaperIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
} from "@heroicons/react/24/outline";


const NEWS_BACKEND = "https://aaapd.org.et/gallery_backend/api/news.php";


const Pill = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-sky-700/10 text-sky-800 border border-sky-700/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
    {children}
  </span>
);

const Bullet = ({ icon: Icon, title, children }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0 mt-1">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-orange-500/10">
        <Icon className="h-5 w-5 text-orange-600" />
      </span>
    </div>
    <div>
      <h4 className="text-base font-semibold text-slate-900">{title}</h4>
      <p className="text-slate-600 text-sm leading-relaxed">{children}</p>
    </div>
  </div>
);

const NewsCard = ({
  title,
  excerpt,
  date,
  author,
  image,
  category,
  link,
  id,
  external = false,
}) => {
  const { t } = useLanguageContext();
  const buttonClasses =
    "mt-4 inline-flex items-center text-sky-700 hover:text-sky-900 font-medium text-sm";

  const content = (
    <>
      {t('readMore')} <ArrowRightIcon className="h-4 w-4 ml-1" />
    </>
  );

  return (
    <article className="bg-white rounded-xl shadow-sm ring-1 ring-slate-200/70 hover:shadow-lg transition-all duration-300 overflow-hidden">
      <div className="relative h-48 md:h-56">
        <img src={image} alt={title} className="w-full h-full object-cover" />
        <div className="absolute top-3 left-3">
          <Pill>{category}</Pill>
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-lg font-semibold text-slate-900 line-clamp-2">
          {title}
        </h3>
        <p className="mt-2 text-sm text-slate-600 line-clamp-3">{excerpt}</p>

        <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
          <span className="inline-flex items-center">
            <CalendarIcon className="h-4 w-4 mr-1" />
            {date}
          </span>
          <span className="inline-flex items-center">
            <UserIcon className="h-4 w-4 mr-1" />
            {author}
          </span>
        </div>

        {link && (external || !excerpt) ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className={buttonClasses}
          >
            {content}
          </a>
        ) : (
          <Link to={link || `/news/${id}`} className={buttonClasses}>
            {content}
          </Link>
        )}
      </div>
    </article>
  );
};

/* -------------------------------------------------------
   Page
------------------------------------------------------- */

const NewsPage = () => {
  const { t } = useLanguageContext();

  // Local AAAPD news from your backend
  const [newsArticles, setNewsArticles] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loadingLocal, setLoadingLocal] = useState(true);

  // Global & Ethiopia disability news (via backend proxy) 
  const [globalDisNews, setGlobalDisNews] = useState([]);
  const [ethiopiaDisNews, setEthiopiaDisNews] = useState([]);
  const [loadingGlobal, setLoadingGlobal] = useState(true);
  const [loadingEthiopia, setLoadingEthiopia] = useState(true);


  useEffect(() => {
    // 1) Local AAAPD news (your PHP backend)
    axios
      .get("https://aaapd.org.et/gallery_backend/news/getNews.php")
      .then((res) => {
        const list = res.data || [];
        if (list.length) {
          setFeatured(list[0]);
          setNewsArticles(list.slice(1));
        }
      })
      .catch((err) => console.error("Error fetching local news:", err))
      .finally(() => setLoadingLocal(false));

   // Helper to robustly map article to card format
    const mapArticle = (a) => ({
      id: a.id || a.url || a.link || Math.random().toString(36).slice(2),
      title: a.title || a.headline || "",
      excerpt: a.excerpt || a.description || a.content || "",
      date: (() => {
        const d = a.date || a.publishedAt || a.published_at;
        return d ? new Date(d).toLocaleDateString() : "";
      })(),
      author: a.author || (a.source && (a.source.name || a.source)) || t('newsSource'),
      image: a.image || a.urlToImage || a.thumbnail || "/newsfallback.jpg",
      url: a.url || a.link || "#",
    });

    // 2) Global disability news via backend
    fetch(NEWS_BACKEND)
      .then((r) => r.json())
      .then((json) => {
        const raw = Array.isArray(json)
          ? json
          : Array.isArray(json.articles)
          ? json.articles
          : Array.isArray(json.data)
          ? json.data
          : [];
        setGlobalDisNews(raw.map(mapArticle));
      })
      .catch((err) => console.error("Error fetching global disability news:", err))
      .finally(() => setLoadingGlobal(false));

      // 3) Ethiopia-focused disability news via backend with query param
    const ethiopiaQ = 'Ethiopia AND ("persons with disabilities" OR disability OR "disability rights")';
    fetch(`${NEWS_BACKEND}?q=${encodeURIComponent(ethiopiaQ)}`)
      .then((r) => r.json())
      .then((json) => {
        const raw = Array.isArray(json)
          ? json
          : Array.isArray(json.articles)
          ? json.articles
          : Array.isArray(json.data)
          ? json.data
          : [];
        setEthiopiaDisNews(raw.map(mapArticle));
      })
      .catch((err) => console.error("Error fetching Ethiopia disability news:", err))
      .finally(() => setLoadingEthiopia(false));
  }, []);

  return (
    <div className="bg-slate-50 text-slate-800">
       {/* SEO Meta Tags */}
      <Helmet>
        <title>AAAPD News – Latest Updates on Disability Rights & Advocacy</title>
        <meta
          name="description"
          content="Stay updated with the latest news, advocacy, and disability rights developments from AAAPD, Ethiopia, and worldwide. Read local and global disability news, featured articles, and breaking updates."
        />
        <link rel="canonical" href="https://aaapd.org.et/news" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <HomeNav />

      <main>
        {/* HERO – banner */}
        <section
          className="relative py-24 md:py-48 bg-cover bg-center text-white"
          style={{ backgroundImage: "url('/newsimage2.png')" }}
        >
          <div className="absolute inset-0 bg-slate-800 bg-opacity-70" />
          <div className="relative max-w-6xl mx-auto px-6 text-center z-10">
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">{t('newsPageTitle')}</h1>
            <p className="mt-6 text-lg md:text-xl text-teal-100 max-w-4xl mx-auto leading-relaxed">
              {t('newsPageSubtitle')}
            </p>
          </div>
        </section>


        {/* DISABILITY NEWS – ETHIOPIA & GLOBAL (external API) */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-7xl mx-auto px-6 text-white">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <Pill>
                  <span className="flex items-center gap-2">
                    <GlobeAltIcon className="h-4 w-4" />
                    {t('disabilityNewsPill')}
                  </span>
                </Pill>
                <h2 className="mt-3 text-3xl md:text-4xl font-bold">
                  {t('disabilityHeadlinesTitle')}
                </h2>
                <p className="mt-2 text-slate-200 text-sm md:text-base max-w-2xl">
                  {t('disabilityHeadlinesDesc')}
                </p>
              </div>
              <div className="text-xs md:text-sm text-slate-300">
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
                  {t('ethiopiaFocusLabel')} &nbsp;|&nbsp;
                  <span className="inline-block w-2 h-2 rounded-full bg-sky-400" />
                  {t('globalPerspectiveLabel')}
                </span>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-10">
              {/* Column 1–2: Ethiopia disability news */}
              <div className="lg:col-span-2">
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
                  {t('ethiopiaSectionTitle')}
                </h3>

                {loadingEthiopia ? (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="h-40 rounded-xl bg-slate-800/40 animate-pulse"
                      />
                    ))}
                  </div>
                ) : ethiopiaDisNews.length ? (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {ethiopiaDisNews.map((a) => (
                      <NewsCard
                        key={a.id}
                        title={a.title}
                        excerpt={a.excerpt}
                        date={a.date}
                        author={a.author}
                        image={a.image}
                        category={t('ethiopiaCategoryLabel')}
                        link={a.url}
                        external
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-300 text-sm">
                    {t('ethiopiaEmpty')}
                  </p>
                )}
              </div>

              {/* Column 3: Global disability news – vertical scroll */}
              <div className="lg:col-span-1">
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                  <span className="inline-block w-2 h-2 rounded-full bg-sky-400" />
                  {t('globalHighlightsTitle')}
                </h3>


                <div className="bg-slate-800/60 rounded-xl p-4 max-h-[480px] overflow-y-auto space-y-4">
                  {loadingGlobal ? (
                    [...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className="h-24 rounded-lg bg-slate-700/60 animate-pulse"
                      />
                    ))
                  ) : globalDisNews.length ? (
                    globalDisNews.map((a) => (
                      <div
                        key={a.id}
                        className="border border-slate-700/80 rounded-lg p-3 hover:border-sky-500/70 transition-all"
                      >
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <p className="text-sm font-semibold line-clamp-2">
                            {a.title}
                          </p>
                          <p className="mt-1 text-xs text-slate-300 line-clamp-2">
                            {a.excerpt}
                          </p>
                          <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
                            <span>{a.author}</span>
                            <span>{a.date}</span>
                          </div>
                        </a>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-300 text-sm">
                      {t('globalEmpty')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED NEWS (local from backend) */}
        {featured && (
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-12">
                <Pill>{t('featuredLabel')}</Pill>
                <h2 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900">
                  {t('breakingNews')}
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-10 items-center">
                <div className="relative h-80 rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <Pill>{featured.category}</Pill>
                  </div>
                </div>


                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-slate-900">
                    {featured.title}
                  </h3>
                  <p className="mt-3 text-slate-600 text-lg">
                    {featured.excerpt}
                  </p>
                  <div className="mt-4 flex items-center gap-6 text-sm text-slate-500">
                    <span className="inline-flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {featured.date}
                    </span>
                    <span className="inline-flex items-center">
                      <UserIcon className="h-4 w-4 mr-1" />
                      {featured.author}
                    </span>
                  </div>
                  {(!featured?.description && featured?.link) ? (
                    <a
                      href={featured.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-flex items-center px-6 py-3 rounded-lg text-base font-semibold bg-sky-700 text-white hover:bg-sky-800 transition-colors"
                    >
                      {t('readFullStory')}
                      <ArrowRightIcon className="h-5 w-5 ml-2" />
                    </a>
                  ) : (
                    <Link
                      to={`/news/${featured.id}`}
                      className="mt-6 inline-flex items-center px-6 py-3 rounded-lg text-base font-semibold bg-sky-700 text-white hover:bg-sky-800 transition-colors"
                    >
                      {t('readFullStory')}
                      <ArrowRightIcon className="h-5 w-5 ml-2" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* RECENT NEWS GRID (local) */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{t('recentUpdates')}</h2>
                <p className="mt-2 text-slate-600">{t('recentUpdatesDesc')}</p>
              </div>
              <Link
                to="/contactus"
                className="hidden md:inline-flex items-center px-5 py-2.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-700"
              >
                {t('subscribeNow')}
              </Link>
            </div>

            {loadingLocal ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="h-72 rounded-xl bg-slate-200 animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {newsArticles.map((n) => (
                  <NewsCard
                    key={n.id}
                    title={n.title}
                    excerpt={n.excerpt}
                    date={n.date}
                    author={n.author}
                    image={n.image}
                    category={n.category}
                    id={n.id}
                    link={n.link}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* QUOTE BANNER */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">{t('newsQuoteBanner')}</h3>
          </div>
        </section>


        {/* SECTION A - Advocacy & Policy */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{t('advocacyPolicyTitle')}</h2>
              <div className="mt-8 space-y-6">
                <Bullet icon={MegaphoneIcon} title={t('advocacyAwarenessTitle')}>
                  {t('advocacyAwarenessDesc')}
                </Bullet>
                <Bullet icon={ScaleIcon} title={t('advocacyPolicyEngTitle')}>
                  {t('advocacyPolicyEngDesc')}
                </Bullet>
                <Bullet icon={NewspaperIcon} title={t('advocacyMediaTitle')}>
                  {t('advocacyMediaDesc')}
                </Bullet>
              </div>
            </div>

            <div className="relative">
              <img
                src={"/aaapdmainlogo.jpg"}
                alt={t('advocacyImageAlt')}
                className="w-full h-[380px] object-contain rounded-xl shadow-lg bg-white"
              />
              <p className="sr-only">{t('advocacyImageSr')}</p>
            </div>
          </div>
        </section>

        {/* SECTION B - Inclusiveness & Capacity Building */}
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
            <div className="order-2 md:order-1">
              <img
                src={"/relation.png"}
                alt="AAAPD participation in decision-making"
                className="w-full h-[380px] object-contain rounded-xl shadow-lg bg-white"
              />
            </div>

            <div className="order-1 md:order-2">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{t('inclusivenessTitle')}</h2>
              <div className="mt-8 space-y-6">
                <Bullet icon={AcademicCapIcon} title={t('inclusivenessTrainingTitle')}>
                  {t('inclusivenessTrainingDesc')}
                </Bullet>
                <Bullet icon={UsersIcon} title={t('inclusivenessNetworkTitle')}>
                  {t('inclusivenessNetworkDesc')}
                </Bullet>
                <Bullet icon={ShieldCheckIcon} title={t('inclusivenessMonitoringTitle')}>
                  {t('inclusivenessMonitoringDesc')}
                </Bullet>
                <Bullet icon={BuildingLibraryIcon} title={t('inclusivenessLegalTitle')}>
                  {t('inclusivenessLegalDesc')}
                </Bullet>
              </div>
            </div>
          </div>
        </section>


        {/* MINI GRID of supportive bullets */}
        <section className="py-10 bg-white">
          <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Bullet icon={GlobeAltIcon} title={t('miniLinkageTitle')}>
              {t('miniLinkageDesc')}
            </Bullet>
            <Bullet icon={DocumentTextIcon} title={t('miniEvidenceTitle')}>
              {t('miniEvidenceDesc')}
            </Bullet>
            <Bullet icon={ChartBarIcon} title={t('miniDataTitle')}>
              {t('miniDataDesc')}
            </Bullet>
            <Bullet icon={BanknotesIcon} title={t('miniResourceTitle')}>
              {t('miniResourceDesc')}
            </Bullet>
          </div>
        </section>

        {/* CTA STRIP */}
        <section className="bg-sky-900">
          <div className="max-w-6xl mx-auto px-6 py-12 text-center text-white">
            <h3 className="text-2xl font-bold">{t('stayInformed')}</h3>
            <p className="mt-2 text-sky-200">
              {t('stayInformedDesc')}
            </p>
            <div className="mt-6">
              <Link
                to="/contactus"
                className="inline-flex items-center px-6 py-3 rounded-md font-semibold bg-white text-sky-900 hover:bg-slate-100"
              >
                {t('subscribeNow')}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NewsPage;
